import { z } from 'zod';

/**
 * Regulatory obligations catalog types: the hierarchy
 * (regulator → standard → chapter) produced by the rulebook-ingestion service
 * and written to S3 as `catalog.json`, read back via the S3 claim-check.
 *
 * Shared by the producer (rulebook-ingestion), the data-layer reader, and the
 * web catalog picker (via tRPC).
 *
 * `runId` / `providerName` are plain strings here: the producer assigns branded
 * values (assignable to `string`); consumers only read them.
 */
const catalogItemSchema = z.object({
  externalId: z.string(),
  externalParentId: z.string().optional().nullable(),
  title: z.string(),
});

export type CatalogItem = z.infer<typeof catalogItemSchema>;

const catalogRegulatorEntrySchema = z.object({
  regulatorId: z.string(),
  regulatorName: z.string(),
  standards: z.array(catalogItemSchema),
  chapters: z.array(catalogItemSchema),
});

export type CatalogRegulatorEntry = z.infer<typeof catalogRegulatorEntrySchema>;

export const catalogSchema = z.object({
  runId: z.string(),
  providerName: z.string(),
  regulators: z.array(catalogRegulatorEntrySchema),
  createdAt: z.string(),
});

export type Catalog = z.infer<typeof catalogSchema>;

/**
 * Flattened catalog node (id, title, parentId, type). `parentId` is null for
 * regulators (tree roots).
 */
export const catalogNodeTypeSchema = z.enum([
  'regulator',
  'standard',
  'chapter',
]);

export type CatalogNodeType = z.infer<typeof catalogNodeTypeSchema>;

export const catalogNodeSchema = z.object({
  externalId: z.string(),
  title: z.string(),
  parentId: z.string().nullable(),
  type: catalogNodeTypeSchema,
});

export type CatalogNode = z.infer<typeof catalogNodeSchema>;

export const flattenedCatalogSchema = z.array(catalogNodeSchema);
